import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  getGetConvertResultRevenuesQueryKey,
  useGetConvertResultExpenditureOptions,
  usePatchSettlementStatisticsRevenue,
} from '@/entities/settlementstatisticsconverter/api/generated/api';
import type { RevenueRecordResponse } from '@/entities/settlementstatisticsconverter/api/generated/model';
import { buildSettlementCascaderOptions } from '@/entities/settlementstatisticsconverter/lib/buildSettlementCascaderOptions';
import {
  RecurringTypeValue,
  recurringTypeSelectOptions,
} from '@/entities/settlementstatisticsconverter/lib/recurring-type';
import {
  RevenueSourceTypeValue,
  revenueSourceTypeSelectOptions,
} from '@/entities/settlementstatisticsconverter/lib/revenue-source-type';
import { formatCodeNamePairs } from '@/shared/lib/format';
import {
  Button,
  CascaderField,
  Form,
  LabeledText,
  SelectField,
  Skeleton,
  Text,
  toast,
  useSkeletonMinDisplay,
} from '@/shared/ui';
import { SideMenuFormFooter } from '@/widgets/right-side-menu/ui/SideMenuFormFooter';
import { type RevenueEditFormValues, revenueEditSchema } from './schema';

type RevenueEditFormProps = {
  /** 変換結果ID */
  convertResultId: string;
  /** 編集対象の歳入レコード */
  selectedRevenue: RevenueRecordResponse;
  /** パネルを閉じる */
  onClose: () => void;
};

/**
 * 歳入コード振り結果 訂正フォーム
 *
 * 歳入区分、臨時経常区分、財源区分の3フィールドを編集するフォーム。
 * 離脱アラート（leavePageAlertCondition）を含む。
 */
export const RevenueEditForm = ({
  convertResultId,
  selectedRevenue,
  onClose,
}: RevenueEditFormProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // 歳入区分オプション取得
  const { data: optionsData, isLoading: isOptionsLoading } = useGetConvertResultExpenditureOptions(
    convertResultId,
    { fields: ['revenue_settlement_purpose'] },
  );

  // 歳入区分のCascaderオプション
  const settlementPurposeOptions = buildSettlementCascaderOptions({
    settlements: optionsData?.revenue_settlement_purposes,
  });

  // 初期値を設定（コードパス）
  const defaultSettlementPurposeCodePath = selectedRevenue.settlement_purpose_value
    ? [
        selectedRevenue.settlement_purpose_value.level1_code,
        selectedRevenue.settlement_purpose_value.level2_code,
        selectedRevenue.settlement_purpose_value.level3_code,
        selectedRevenue.settlement_purpose_value.level4_code,
      ].filter(Boolean)
    : [];

  const form = useForm<RevenueEditFormValues>({
    resolver: zodResolver(revenueEditSchema),
    defaultValues: {
      settlement_purpose_code_path: defaultSettlementPurposeCodePath,
      recurring_type_value:
        (selectedRevenue.recurring_type_value as RecurringTypeValue) ?? RecurringTypeValue.経常,
      revenue_source_type_value:
        (selectedRevenue.revenue_source_type_value as RevenueSourceTypeValue) ??
        RevenueSourceTypeValue.特定財源,
    },
  });

  const { mutate: patchRevenue, isPending } = usePatchSettlementStatisticsRevenue();

  const handleSubmit = (values: RevenueEditFormValues) => {
    // 決算統計目的: codePath → UUID
    const purposeCodePath = values.settlement_purpose_code_path;
    const settlementPurposeValueId = optionsData?.revenue_settlement_purposes?.find(
      (opt) =>
        opt.level1_code === purposeCodePath[0] &&
        (purposeCodePath[1] ? opt.level2_code === purposeCodePath[1] : true) &&
        (purposeCodePath[2] ? opt.level3_code === purposeCodePath[2] : true) &&
        (purposeCodePath[3] ? opt.level4_code === purposeCodePath[3] : true),
    )?.id;

    if (!settlementPurposeValueId) {
      toast.error(t('common.messages.update-failed', { name: '歳入コード振り結果' }));
      return;
    }

    patchRevenue(
      {
        convertResultId,
        revenueId: selectedRevenue.id,
        data: {
          settlement_purpose_value_id: settlementPurposeValueId,
          recurring_type_value: values.recurring_type_value,
          revenue_source_type_value: values.revenue_source_type_value,
        },
      },
      {
        onSuccess: () => {
          toast.success(t('common.messages.update-success', { name: '歳入コード振り結果' }));
          queryClient.invalidateQueries({
            queryKey: getGetConvertResultRevenuesQueryKey(convertResultId),
          });
          onClose();
        },
        onError: () => {
          toast.error(t('common.messages.update-failed', { name: '歳入コード振り結果' }));
        },
      },
    );
  };

  const showSkeleton = useSkeletonMinDisplay(isOptionsLoading);
  if (showSkeleton) {
    return (
      <div className="space-y-4 p-4">
        <Skeleton className="h-6 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  return (
    <Form.Provider {...form}>
      <Form
        leavePageAlertCondition={{
          dirtyFields: form.formState.dirtyFields,
          isSubmitting: form.formState.isSubmitting,
          isSubmitSuccessful: form.formState.isSubmitSuccessful,
        }}
        onSubmit={form.handleSubmit(handleSubmit)}
        className="flex min-h-0 flex-1 flex-col"
      >
        {/* スクロール可能なフォーム領域 */}
        <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-4 py-4">
          {/* 行情報（読み取り専用） */}

          <LabeledText label={t('common.fields.accounting-type')}>
            {formatCodeNamePairs([
              [selectedRevenue.accounting_type_code, selectedRevenue.accounting_type_name],
            ])}
          </LabeledText>
          <LabeledText label={t('settlement-statistics-converter.common-fields.revenue-category')}>
            {formatCodeNamePairs([
              [selectedRevenue.kan_code, selectedRevenue.kan_name],
              [selectedRevenue.ko_code, selectedRevenue.ko_name],
              [selectedRevenue.moku_code, selectedRevenue.moku_name],
              [selectedRevenue.setsu_code, selectedRevenue.setsu_name],
              [selectedRevenue.saisetsu_code, selectedRevenue.saisetsu_name],
              [selectedRevenue.saisaisetsu_code, selectedRevenue.saisaisetsu_name],
            ])}
          </LabeledText>
          <LabeledText label={t('common.fields.description')}>
            {selectedRevenue.description}
          </LabeledText>
          <LabeledText label={t('common.fields.amount')}>
            <Text viewMode="number">{selectedRevenue.amount}</Text>
          </LabeledText>

          {/* 編集フィールド */}
          <CascaderField
            control={form.control}
            name="settlement_purpose_code_path"
            label={t('settlement-statistics-converter.common-fields.settlement-purpose-revenue')}
            options={settlementPurposeOptions}
            showSearch
            showAllClear
            onlyLeafSelectable
            required
          />

          <SelectField
            control={form.control}
            name="recurring_type_value"
            label={t('common.fields.recurring-type')}
            options={recurringTypeSelectOptions}
            required
          />

          <SelectField
            control={form.control}
            name="revenue_source_type_value"
            label={t('settlement-statistics-converter.revenue.revenue-source-type')}
            options={revenueSourceTypeSelectOptions}
            required
          />
        </div>

        <SideMenuFormFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onClose();
            }}
            disabled={isPending}
          >
            {t('common.actions.cancel')}
          </Button>
          <Button type="submit" disabled={isPending}>
            {t('common.actions.save')}
          </Button>
        </SideMenuFormFooter>
      </Form>
    </Form.Provider>
  );
};
